import type { SavedSpecimenRef } from "../components/frozen-storage-data";
import type { StorageLocationPatch } from "./useSpecimens";

/**
 * What a reopened intake does about rows that already exist.
 *
 * It updates them — but only once the form reopens faithful to what it wrote,
 * because it writes the whole record at once and any field it could not read
 * back would be saved as a blank. That fidelity is `parseNotesString` and the
 * write-read-write invariant in `notesRoundTrip.test.ts`; what lives here is
 * the naming an update needs so the server reconciles instead of appending, and
 * the retiring a smaller count implies.
 *
 * Both are one-line judgements with expensive consequences — one raised
 * `Conflict` on every item of the batch when it was got wrong, the other let
 * the vault go on counting eggs she had already taken back — and neither is
 * reachable through the hook.
 */

/** Which rows a reduction may retire, and what to close on them. */
export type SavedSpecimenRefs = Record<string, SavedSpecimenRef> | undefined;

/**
 * The placement to send for one row, naming the episode it already has.
 *
 * Unnamed, an update asks for a second episode and `createStorageEpisode`
 * refuses it while an unresolved one is open — the `Conflict`, `retryable:
 * false`, on every item of the batch that `de53cd8ae` removed. Named, the
 * server reconciles the placement she has.
 *
 * When the row is no longer stored there is nothing to send BUT something to
 * end: the episode is closed outright, or the row would leave the vault on its
 * status while its placement stayed open — and the next reopen would read
 * "stored" straight back off it and contradict what she just saved.
 */
export function placementForRow<T extends object>(
  storageLocations: T[] | undefined,
  existingId: string | undefined,
  refs: SavedSpecimenRefs,
): { storageLocations?: unknown[] } {
  const episodeId = existingId
    ? refs?.[existingId]?.storageLocationId
    : undefined;
  if (!storageLocations) {
    return episodeId
      ? { storageLocations: [{ id: episodeId, op: "delete" }] }
      : {};
  }
  if (!episodeId) return { storageLocations };
  return {
    storageLocations: storageLocations.map((location) => ({
      ...location,
      id: episodeId,
    })),
  };
}

/**
 * One recorded event for one row, naming the one it already has.
 *
 * The batch accepts `id` on usages exactly as on placements, so an unnamed one
 * is appended rather than reconciled. On a form that could only add that never
 * showed; once reopen-and-save is ordinary, it leaves one more freeze, thaw or
 * biopsy per save in her clinical record.
 */
/** Where each event kind keeps its nested record. */
const USAGE_DETAIL_FIELD = {
  freeze: "freezingInfo",
  thaw: "thawingInfo",
  biopsy: "embryoBiopsy",
} as const;

export function usageForRow<T extends { usage?: unknown }>(
  usage: T,
  existingId: string | undefined,
  refs: SavedSpecimenRefs,
): T & { id?: string } {
  const kind = usage.usage;
  if (kind !== "freeze" && kind !== "thaw" && kind !== "biopsy") return usage;
  const known = existingId ? refs?.[existingId]?.usageIds?.[kind] : undefined;
  if (!known) return usage;

  const named: Record<string, unknown> = { ...usage, id: known.id };
  /*
   * The nested record has to be named too, or not sent at all.
   *
   * `handleFreezingInfo` and its siblings branch on the nested id alone: given
   * none, they insert — against a `specimen_usage_id` that is
   * `notNull().unique()`. The constraint violation is a raw database error
   * rather than an `ApiError`, so the batch reports it as `internal_error` on
   * every item, which is exactly what naming the usage and forgetting its
   * detail produced in the browser.
   */
  const field = USAGE_DETAIL_FIELD[kind];
  const detail = named[field];
  if (detail && typeof detail === "object") {
    named[field] = known.detailId ? { ...detail, id: known.detailId } : detail;
    // No known detail id means the row has a usage but no nested record yet,
    // so inserting one is right.
  }
  return named as T & { id?: string };
}

/** What one row she has taken back is told to become. */
export type SurplusClosure = {
  specimenId: string;
  /** The index the row held, so a retry keeps the same item identity. */
  index: number;
  data: {
    status: "processed";
    storageLocations?: StorageLocationPatch[];
  };
};

/**
 * The rows a smaller count leaves behind, ended rather than abandoned.
 *
 * The write loop runs to the count now on the form, so saving 6 where 8 were
 * recorded simply never reached the last two: they stayed `stored` with an open
 * placement, and the vault — which counts `stored` rows with an active episode
 * — went on showing eight. A correction has to reach them.
 *
 * A count of zero closes nothing, deliberately. `isValidSpecimenCycle` refuses
 * to save a cycle whose frozen count is not above zero, so zero here never
 * means "she said none" — it means the form wrote no rows at all, and a form
 * that recorded nothing must not retire what she already had. The individual
 * embryo path counts its entries rather than that field, so this is the guard
 * that covers it.
 *
 * The placement is closed with `op: "delete"` and the row falls to `processed`,
 * so the vault stops counting it while her record keeps it. Deliberately not a
 * specimen delete: `endReason` offers only thawed, transferred out, discarded
 * and donated, and none of those happened — she is correcting a number, not
 * reporting an event. Keeping the row also keeps the correction reversible,
 * which a hard delete would not, and keeps every write in the one batch, so a
 * correction is atomic with the save it belongs to and replays under the same
 * operation key.
 */
export function surplusClosures(
  existingIds: string[],
  keptCount: number,
  refs: SavedSpecimenRefs,
): SurplusClosure[] {
  if (keptCount <= 0) return [];
  const closures: SurplusClosure[] = [];
  for (let index = keptCount; index < existingIds.length; index++) {
    const specimenId = existingIds[index]!;
    const ref = refs?.[specimenId];
    /*
     * No entry means the row is not hers to retire from here — she has thawed,
     * used, reserved or discarded it. It still held its place so it was never
     * re-created; retiring it would rewrite biology a later cycle has claimed.
     */
    if (!ref) continue;
    const episodeId = ref.storageLocationId;
    closures.push({
      specimenId,
      index,
      data: {
        status: "processed",
        ...(episodeId
          ? { storageLocations: [{ id: episodeId, op: "delete" }] }
          : {}),
      },
    });
  }
  return closures;
}
